import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, X } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import type { RSVP, CreateRSVPPayload, CreateGuestPayload } from "@/services/api/eventSettings";

interface RsvpModalProps {
  open: boolean;
  mode: "create" | "edit";
  rsvp?: RSVP;
  onOpenChange: (open: boolean) => void;
  onSubmit: (payload: CreateRSVPPayload | { id: string; payload: Partial<CreateRSVPPayload> }) => Promise<void>;
  loading?: boolean;
}

export function RsvpModal({ open, onOpenChange, mode, rsvp, onSubmit, loading = false }: RsvpModalProps) {
  const { t } = useTranslation();
  const [rsvpName, setRsvpName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [tableName, setTableName] = useState("");
  const [status, setStatus] = useState<"confirmed" | "pending" | "declined">("pending");
  const [guests, setGuests] = useState<CreateGuestPayload[]>([]);

  useEffect(() => {
    if (rsvp && mode === "edit") {
      setRsvpName(rsvp.familyName || "");
      setEmail(rsvp.email || "");
      setPhone(rsvp.phone || "");
      setTableName(rsvp.tableName || "");
      setStatus(rsvp.status || "pending");
      // In edit mode, we don't pre-populate guests (they're managed separately)
      setGuests([]);
    } else {
      setRsvpName("");
      setEmail("");
      setPhone("");
      setTableName("");
      setStatus("pending");
      setGuests([]);
    }
  }, [rsvp, mode, open]);

  const addGuest = () => {
    setGuests([
      ...guests,
      {
        name: "",
        type: "adult",
      },
    ]);
  };

  const removeGuest = (index: number) => {
    setGuests(guests.filter((_, i) => i !== index));
  };

  const updateGuest = (index: number, updates: Partial<CreateGuestPayload>) => {
    setGuests(guests.map((guest, i) => (i === index ? { ...guest, ...updates } : guest)));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // JUB-85: email is optional for organizer-created RSVPs, but the group still
    // needs a label — require a family name or an email.
    if (!email.trim() && !rsvpName.trim()) {
      toast.error(t("guests.familyNameOrEmailRequired"));
      return;
    }

    // Validate guests if any are added
    const validGuests = guests.filter((g) => g.name.trim() !== "");
    if (guests.length > 0 && validGuests.length === 0) {
      toast.error(t("guests.guestNameRequired") || t("validation.nameRequired") || "Guest name is required");
      return;
    }

    const payload: CreateRSVPPayload = {
      email: email.trim() || undefined,
      phone: phone.trim() || undefined,
      familyName: rsvpName.trim() || undefined,
      tableName: tableName.trim() || null,
      status: status,
      guests: validGuests, // Include guests if provided (atomic creation)
    };

    try {
      if (mode === "edit" && rsvp) {
        await onSubmit({ id: rsvp.id, payload });
      } else {
        await onSubmit(payload);
      }
      // Modal will be closed by parent component after successful submission
    } catch (error) {
      console.error(error);
      // Error is already handled in the parent component (GuestsTab)
      // Don't close modal on error so user can retry
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {mode === "create"
              ? t("guests.createNewRsvp") || t("guests.createRsvp")
              : t("guests.editRsvp")}
          </DialogTitle>
          <DialogDescription>
            {mode === "create"
              ? t("guests.createRsvpDescription") || t("guests.createDescription")
              : t("guests.editRsvpDescription") || t("guests.editDescription")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} data-testid="rsvp-form">
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="rsvpName">{t("guests.rsvpName") || t("guests.name")}</Label>
              <Input
                id="rsvpName"
                data-testid="rsvp-name-input"
                value={rsvpName}
                onChange={(e) => setRsvpName(e.target.value)}
                placeholder={t("guests.rsvpNamePlaceholder") || t("guests.namePlaceholder")}
              />
              <p className="text-sm text-muted-foreground">
                {t("guests.rsvpNameHint") || t("guests.nameHint")}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">
                {t("guests.emailAddress") || t("rsvp.email")}{" "}
                <span className="text-muted-foreground font-normal">{t("guests.optional")}</span>
              </Label>
              <Input
                id="email"
                data-testid="rsvp-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="contact@jubiel.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">{t("guests.phoneNumber") || t("rsvp.phone")}</Label>
              <Input
                id="phone"
                data-testid="rsvp-phone-input"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+40 7XX XXX XXX"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="tableName">
                {t("guests.tableName")}{" "}
                <span className="text-muted-foreground font-normal">{t("guests.optional")}</span>
              </Label>
              <Input
                id="tableName"
                data-testid="rsvp-table-name-input"
                value={tableName}
                onChange={(e) => setTableName(e.target.value)}
                placeholder={t("guests.tableNamePlaceholder")}
              />
            </div>

            {/* JUB-85: status is settable at creation too — phone-confirmed families
                no longer need a post-create edit round-trip */}
            <div className="space-y-2">
              <Label htmlFor="status">{t("guests.rsvpStatus") || t("rsvp.status")}</Label>
              <Select value={status} onValueChange={(v) => setStatus(v as typeof status)}>
                <SelectTrigger data-testid="rsvp-status-select">
                  {/* Explicit children: Radix renders the raw value ("pending") for a
                      default that was never picked from the open list (JPI complaint) */}
                  <SelectValue>{t(`events.form.status.${status}`) || status}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">
                    {t("events.form.status.pending") || t("guests.pendingRsvps")}
                  </SelectItem>
                  <SelectItem value="confirmed">
                    {t("events.form.status.confirmed") || t("guests.confirmedGuests")}
                  </SelectItem>
                  <SelectItem value="declined">
                    {t("events.form.status.declined") || t("guests.declined")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {mode === "create" && (
              <div className="space-y-3 border-t pt-4">
                <div className="flex items-center justify-between">
                  <Label>{t("events.form.guests")}</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addGuest}
                    className="h-8"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    {t("guests.addGuest") || "Add Guest"}
                  </Button>
                </div>
                {guests.length > 0 && (
                  <div className="space-y-3">
                    {guests.map((guest, index) => (
                      <div key={index} className="rounded-md border p-3 space-y-2">
                        <div className="flex gap-2 items-start">
                          <div className="flex-1 grid gap-2 md:grid-cols-2">
                            <Input
                              placeholder={t("guests.guestNamePlaceholder") || "Guest name"}
                              value={guest.name}
                              onChange={(e) => updateGuest(index, { name: e.target.value })}
                            />
                            <Select
                              value={guest.type}
                              onValueChange={(v) => updateGuest(index, { type: v as "adult" | "kid" })}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="adult">{t("guests.adult") || "Adult"}</SelectItem>
                                <SelectItem value="kid">{t("guests.kid") || "Kid"}</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeGuest(index)}
                            className="h-9 w-9"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="grid gap-2 md:grid-cols-2">
                          <Select
                            value={guest.menuPreference || ""}
                            onValueChange={(v) => updateGuest(index, { menuPreference: v ? (v as CreateGuestPayload["menuPreference"]) : undefined })}
                          >
                            <SelectTrigger className="h-8 text-xs">
                              <SelectValue placeholder={t("guests.noPreference") || "No preference"} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="">{t("guests.noPreference") || "No preference"}</SelectItem>
                              <SelectItem value="standard">{t("guests.standardFull") || t("rsvp.menu.standard")}</SelectItem>
                              <SelectItem value="vegetarian">{t("guests.vegetarianFull") || t("rsvp.menu.vegetarian")}</SelectItem>
                              <SelectItem value="vegan">{t("guests.veganFull") || t("rsvp.menu.vegan")}</SelectItem>
                            </SelectContent>
                          </Select>
                          <div className="flex items-center space-x-2 h-8">
                            <input
                              type="checkbox"
                              id={`rsvp-guest-accommodation-${index}`}
                              checked={guest.accommodationNeeded || false}
                              onChange={(e) => updateGuest(index, { accommodationNeeded: e.target.checked || undefined })}
                              className="h-4 w-4 rounded border-gray-300"
                            />
                            <Label htmlFor={`rsvp-guest-accommodation-${index}`} className="text-xs font-normal cursor-pointer">
                              {t("guests.accommodation") || t("rsvp.accommodationNeeded")}
                            </Label>
                          </div>
                        </div>
                      </div>
                    ))}
                    <p className="text-xs text-muted-foreground">
                      {t("guests.addGuestsHint") || "You can add guests now or add them later after creating the RSVP group."}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading} data-testid="rsvp-cancel-button">
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={loading} data-testid="rsvp-submit-button">
              {loading ? t("common.loading") : mode === "create" ? (t("guests.createRsvp") || t("guests.createNewRsvp")) : t("common.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

